'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @primitive LoadingState (client)
 * ROLE: status | ARIA: aria-live="polite" on the outer <div>
 * KEYBOARD: non-focusable (spinner is aria-hidden; no interactive elements)
 * FOCUS: LoadingState does NOT move focus on mount. Focus-restoration handoff
 *   is the wrapping container's responsibility. The imperative handle
 *   `loadingStateRef.current?.handoffFocus()` is available for consumer-
 *   orchestrated loads (non-Form, non-Next.js-route patterns). Callers invoke
 *   it AFTER the loaded content has rendered so AT focus lands on content.
 * LIVE-REGION: role="status" aria-live="polite" — announces label once per
 *   announceOn discipline (see below). The live region is always present in
 *   the DOM; the `aria-live` attribute is used instead of toggling presence
 *   to avoid SR races on mount.
 * announceOn:
 *   "mount"      — announce on initial mount only (clears after first tick).
 *   "transition" — announce only when arriving from a non-loading state;
 *                  prevents re-announcement on parent re-render (default).
 *   "always"     — announce on every render (rare).
 * PRM: inline spinner uses opacity pulse instead of rotate
 * FORCED-COLORS: spinner arc → ButtonText; label → ButtonText
 * RTL: direction-agnostic
 * RSC: CLIENT
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, } from 'react';
import { Spinner } from '../.././../server/state/Spinner/Spinner';
import styles from './LoadingState.module.css';
function findFirstFocusable(container) {
    return container.querySelector('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
}
export const LoadingState = forwardRef(function LoadingState({ placement = 'inset', label = 'Loading', announceOn = 'transition', className, }, ref) {
    const isMountedRef = useRef(true);
    // "announced" tracks whether the mount-time announcement window has closed.
    // For "always", we never close the window.
    // For "mount"/"transition", we open polite on mount, close after 500ms.
    const [announced, setAnnounced] = useState(false);
    useEffect(() => {
        isMountedRef.current = true;
        if (announceOn !== 'always') {
            // Give AT 500ms to read the label, then silence the live region so
            // subsequent re-renders don't re-announce.
            const id = setTimeout(() => {
                if (isMountedRef.current)
                    setAnnounced(true);
            }, 500);
            return () => clearTimeout(id);
        }
        return undefined;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        return () => { isMountedRef.current = false; };
    }, []);
    useImperativeHandle(ref, () => ({
        handoffFocus(container) {
            const target = findFirstFocusable(container) ?? container;
            if (!container.contains(target) || target === container) {
                container.setAttribute('tabindex', '-1');
            }
            target.focus({ preventScroll: false });
        },
    }));
    const rootCls = [styles.root, styles[`placement-${placement}`], className]
        .filter(Boolean)
        .join(' ');
    const spinnerSize = placement === 'page' ? 'lg' : placement === 'inset' ? 'md' : 'sm';
    // The visible label doubles as the live-region text. We render it always;
    // the aria-live on the container causes AT to re-read its text content
    // when the container mounts. After announcement fires, we clear the
    // aria-live value to silence re-renders.
    return (_jsxs("div", { role: "status", "aria-live": !announced ? 'polite' : 'off', className: rootCls, children: [_jsx(Spinner, { size: spinnerSize, className: styles.spinner }), _jsx("span", { className: styles.label, children: label })] }));
});
//# sourceMappingURL=LoadingState.js.map